import { Box, Stack, Typography } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { DATE_FORMAT } from '../../../consts';
import { useGetAccount } from '../../../hooks/useAccount';
import { useGetAccountReferrals } from '../../../hooks/query/useReferrals';
import STRING_KEYS from '../../../language/keys';
import { AccountReferralPosition } from '../../../@types/Referral';
import { accountReferralPositionsStyle } from './account-referral-positions.style';

type AccountReferralPositionsProps = {
  accountId: string,
};

/**
 * Read-only: why the account's position cap is higher than its plan, and who referred it.
 */
export const AccountReferralPositions = ({ accountId }: AccountReferralPositionsProps): JSX.Element | null => {
  const { t: translate } = useTranslation();
  const { account } = useGetAccount(accountId);
  const { data: accountReferrals, isError } = useGetAccountReferrals(accountId);

  const referralCount = account?.plan?.referralOpenPositions || 0;
  const paidCount = account?.plan?.maximumOpenPositions || 0;
  const totalCap = account?.plan?.totalMaximumOpenPositions ?? paidCount + referralCount;
  const positions = accountReferrals?.referralPositions || [];
  const referredBy = accountReferrals?.referredBy;
  const hasReferralData = referralCount > 0 || positions.length > 0 || !!referredBy;

  if (!hasReferralData) {
    return null;
  }

  const renderPeriod = (position: AccountReferralPosition): string => {
    if (!position.endsAt) {
      return translate(STRING_KEYS.REFERRALS.POSITION_ONGOING);
    }

    return translate(STRING_KEYS.REFERRALS.POSITION_UNTIL, { date: moment(position.endsAt).format(DATE_FORMAT.DATE) });
  };

  return (
    <Box>
      <Typography variant="h6" sx={ accountReferralPositionsStyle.title.sx }>
        { translate(STRING_KEYS.REFERRALS.ACCOUNT_PANEL_TITLE) }
      </Typography>
      <Stack spacing={ 1 }>
        { referralCount > 0 && (
          <Typography variant="body2">
            { translate(STRING_KEYS.REFERRALS.ACCOUNT_PANEL_COUNT, {
              count: referralCount,
              total: totalCap,
              paid: paidCount,
            }) }
          </Typography>
        ) }
        { positions.map((position) => (
          <Typography key={ position.id } variant="body2" color="textSecondary">
            { translate(STRING_KEYS.REFERRALS.ACCOUNT_PANEL_POSITION, {
              company: position.referredCompanyName || translate(STRING_KEYS.REFERRALS.UNKNOWN_COMPANY),
              period: renderPeriod(position),
            }) }
          </Typography>
        )) }
        { referredBy && (
          <Typography variant="body2">
            { translate(STRING_KEYS.REFERRALS.ACCOUNT_PANEL_REFERRED_BY, {
              name: referredBy.referrerName || referredBy.referrerEmail ||
                translate(STRING_KEYS.REFERRALS.UNKNOWN_REFERRER),
            }) }
          </Typography>
        ) }
        { isError && (
          <Typography variant="caption" color="error">
            { translate(STRING_KEYS.REFERRALS.ACCOUNT_PANEL_LOAD_ERROR) }
          </Typography>
        ) }
      </Stack>
    </Box>
  );
};
